import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  GLP1_MEDICATIONS,
  MENTAL_HEALTH_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  VACCINE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Comprehensive pre-screening questions for lp-test study
 * This configuration uses questions with existing attribute mappings for testing actual functionality
 */
export const lpTestScreeningQuestions: PreScreeningQuestion[] = [
  // RADIO question - Diabetes Type (existing mapping)
  {
    id: "diabetesType",
    type: QuestionType.RADIO,
    question: "Do you have diabetes?",
    helpText: "This includes Type 1 diabetes, Type 2 diabetes, or no diabetes",
    required: true,
    allowBack: true,
    options: [
      {
        value: "type2",
        label: "Yes, I have Type 2 diabetes",
      },
      {
        value: "type1",
        label: "Yes, I have Type 1 diabetes",
      },
      {
        value: "none",
        label: "No, I do not have diabetes",
      },
      {
        value: "unsure",
        label: "I'm not sure",
      },
    ],
    attributeMapping: mappers.value({
      type2: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true }],
      type1: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true }],
      none: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
      unsure: [], // No attributes set for unsure
    }),
  },

  // RADIO question - HbA1c Level (existing mapping)
  {
    id: "hba1cLevel",
    type: QuestionType.RADIO,
    question: "What is your most recent HbA1c level?",
    helpText:
      "HbA1c is a blood test that measures your average blood sugar over the past 2-3 months",
    required: true,
    allowBack: true,
    options: [
      {
        value: "6.5_to_11",
        label: "Between 6.5% and 11%",
      },
      {
        value: "7_to_11",
        label: "Between 7% and 11%",
      },
      {
        value: "not_sure",
        label: "I'm not sure",
      },
    ],
    attributeMapping: mappers.value({
      "6.5_to_11": [{ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HBA1C, floatAttributeValue: 8.75 }], // Average of range
      "7_to_11": [{ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HBA1C, floatAttributeValue: 9.0 }], // Average of range
      not_sure: [], // No attributes set for unsure
    }),
  },

  // SELECT question - Height (existing standard mapping)
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  // NUMBER question - Weight (existing standard mapping)
  {
    ...standardWeightQuestion,
    helpText:
      "Please enter your current weight in pounds. This helps us calculate your BMI for eligibility purposes.",
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  // RADIO question - Clinical Trial Participation (existing mapping)
  {
    id: "currentTrial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am in another clinical trial",
        disqualify: true,
      },
      {
        value: "no",
        label: "No, I am not in any other clinical trials",
      },
      {
        value: "unsure",
        label: "I'm not sure",
      },
    ],
    disqualifyMessage:
      "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false }],
      unsure: [], // No attributes set for unsure
    }),
  },

  // RADIO question - In-Person Appointments (existing mapping)
  {
    id: "inPersonAppointments",
    type: QuestionType.RADIO,
    question: "Can you attend in-person study visits over the next few months?",
    helpText: "Most studies require regular visits to a research site near you",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I can attend in-person visits",
      },
      {
        value: "no",
        label: "No, I cannot attend in-person visits",
        disqualify: true,
      },
      {
        value: "maybe",
        label: "Maybe, depending on the schedule",
      },
    ],
    disqualifyMessage:
      "This study requires participants to attend in-person visits.",
    // No attribute mapping needed - this is just for study logistics
  },

  // RADIO question - Anxiety Diagnosis (existing mapping)
  {
    id: "has_anxiety_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with an anxiety disorder?",
    helpText:
      "This includes conditions like generalized anxiety disorder (GAD), social anxiety disorder, panic disorder, or other anxiety-related conditions",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with an anxiety disorder",
      },
      {
        value: "no",
        label: "No, I have NOT been diagnosed with an anxiety disorder",
      },
    ],
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_ANXIETY, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_ANXIETY, booleanAttributeValue: false }],
    }),
  },

  // RADIO question - Depression Status (existing mapping)
  {
    id: "has_depression_current",
    type: QuestionType.RADIO,
    question: "Do you currently have severe depression?",
    helpText:
      "Severe depression significantly impacts your daily life and interferes with your ability to function",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no_depression",
        label: "I don't have severe depression",
      },
      {
        value: "as_depression_severe",
        label: "I have severe depression",
        disqualify: true,
      },
      {
        value: "has_depression_mild",
        label: "I have depression but it is NOT severe",
      },
    ],
    disqualifyMessage:
      "This study cannot include people who are currently experiencing severe depression.",
    attributeMapping: mappers.value({
      no_depression: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: false }],
      as_depression_severe: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: true }],
      has_depression_mild: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: true }],
    }),
  },

  // RADIO_GRID question - Medical Conditions (existing mapping)
  {
    id: "medicalConditions",
    type: QuestionType.RADIO_GRID,
    question: "Do you have any of the following medical conditions?",
    helpText: "Please select the best answer for each condition",
    required: true,
    allowBack: true,
    quickSelect: {
      label: "Submit",
      setValue: "no",
      submitButtonText: "Submit",
      submitButtonSubtext: {
        allText: "All conditions in this group will be marked as 'No'",
        remainingText:
          "Remaining conditions in this group will be marked as 'No'",
      },
      defaultValueSuffix: "_no",
    },
    gridLayout: {
      columnHeaders: ["No", "Not sure", "Yes"],
      rows: [
        {
          label: "Liver cirrhosis",
          values: [
            { value: "cirrhosis_no" },
            { value: "cirrhosis_not_sure" },
            { value: "cirrhosis_yes", disqualify: true },
          ],
        },
        {
          label: "Liver failure",
          values: [
            { value: "liverfailure_no" },
            { value: "liverfailure_not_sure" },
            { value: "liverfailure_yes", disqualify: true },
          ],
        },
        {
          label: "Currently on dialysis",
          values: [
            { value: "dialysis_no" },
            { value: "dialysis_not_sure" },
            { value: "dialysis_yes", disqualify: true },
          ],
        },
        {
          label: "End-stage kidney disease",
          values: [
            { value: "kidney_disease_no" },
            { value: "kidney_disease_not_sure" },
            { value: "kidney_disease_yes", disqualify: true },
          ],
        },
      ],
    },
    disqualifyMessage:
      "For safety reasons, this study cannot include people with these serious medical conditions.",
    attributeMapping: mappers.conditionGrid({
      cirrhosis_no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false }],
      cirrhosis_not_sure: [], // No attribute set for unsure
      cirrhosis_yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true }],
      liverfailure_no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false }],
      liverfailure_not_sure: [], // No attribute set for unsure
      liverfailure_yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true }],
      dialysis_no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false }],
      dialysis_not_sure: [], // No attribute set for unsure
      dialysis_yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true }],
      kidney_disease_no: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false }],
      kidney_disease_not_sure: [], // No attribute set for unsure
      kidney_disease_yes: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true }],
    }),
  },

  // RADIO_GRID question - GLP-1 Medications (existing mapping)
  {
    id: "glp1History",
    type: QuestionType.RADIO_GRID,
    question:
      "Have you ever taken any of the following diabetes/weight loss medications?",
    helpText:
      "These are GLP-1 receptor agonists used for diabetes and weight management",
    required: true,
    allowBack: true,
    quickSelect: {
      label: "Submit",
      setValue: "no",
      submitButtonText: "Submit",
      submitButtonSubtext: {
        allText:
          "All medications in this group will be marked as 'Never taken'",
        remainingText:
          "Remaining medications in this group will be marked as 'Never taken'",
      },
      defaultValueSuffix: "_never",
    },
    gridLayout: {
      columnHeaders: ["Never taken", "Taken in the past", "Currently taking"],
      rows: [
        {
          label: "Semaglutide (Ozempic, Wegovy)",
          values: [
            { value: "semaglutide_never" },
            { value: "semaglutide_past" },
            { value: "semaglutide_current" },
          ],
        },
        {
          label: "Tirzepatide (Mounjaro, Zepbound)",
          values: [
            { value: "tirzepatide_never" },
            { value: "tirzepatide_past" },
            { value: "tirzepatide_current" },
          ],
        },
        {
          label: "Liraglutide (Victoza, Saxenda)",
          values: [
            { value: "liraglutide_never" },
            { value: "liraglutide_past" },
            { value: "liraglutide_current" },
          ],
        },
        {
          label: "Dulaglutide (Trulicity)",
          values: [
            { value: "dulaglutide_never" },
            { value: "dulaglutide_past" },
            { value: "dulaglutide_current" },
          ],
        },
        {
          label: "Exenatide (Byetta, Bydureon)",
          values: [
            { value: "exenatide_never" },
            { value: "exenatide_past" },
            { value: "exenatide_current" },
          ],
        },
      ],
    },
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        semaglutide: GLP1_MEDICATIONS.SEMAGLUTIDE,
        tirzepatide: GLP1_MEDICATIONS.TIRZEPATIDE,
        liraglutide: GLP1_MEDICATIONS.LIRAGLUTIDE,
        dulaglutide: GLP1_MEDICATIONS.DULAGLUTIDE,
        exenatide: GLP1_MEDICATIONS.EXENATIDE,
      },
    }),
  },

  // RADIO question - COVID Vaccination Status (existing mapping)
  {
    id: "covidPrimaryVaccination",
    type: QuestionType.RADIO,
    question: "Have you completed your primary COVID-19 vaccination series?",
    helpText:
      "Primary series means at least 2 doses of an mRNA vaccine (Pfizer or Moderna) or 1 dose of Johnson & Johnson",
    required: true,
    allowBack: true,
    quickSelect: {
      label: "Submit",
      setValue: "no",
      submitButtonText: "Submit",
      submitButtonSubtext: {
        allText:
          "All medications in this group will be marked as 'Never taken'",
        remainingText:
          "Remaining medications in this group will be marked as 'Never taken'",
      },
      defaultValueSuffix: "_never",
    },
    options: [
      {
        value: "yes",
        label: "Yes, I have completed my primary COVID-19 vaccination series",
      },
      {
        value: "no",
        label: "No, I have not completed my primary vaccination series",
        disqualify: true,
      },
      {
        value: "unsure",
        label: "I'm not sure",
      },
    ],
    disqualifyMessage:
      "This study requires participants to have completed their primary COVID-19 vaccination series.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: VACCINE_ATTRIBUTES.HAS_COVID_VACCINE, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: VACCINE_ATTRIBUTES.HAS_COVID_VACCINE, booleanAttributeValue: false }],
      unsure: [], // No attributes set for unsure
    }),
  },

  // SELECT question - Suicide Attempt History (existing mapping)
  {
    id: "has_attempted_suicide_2_years",
    type: QuestionType.SELECT,
    question: "In the past 2 years, have you attempted to take your own life?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have not attempted suicide in the past 2 years",
      },
      {
        value: "yes",
        label: "Yes, I have attempted suicide in the past 2 years",
        disqualify: true,
      },
      {
        value: "prefer_not_answer",
        label: "I prefer not to answer",
      },
    ],
    disqualifyMessage:
      "For safety reasons, this study cannot include people who have recently attempted suicide. If you are currently in crisis, please call 988 for immediate support.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SUICIDE_ATTEMPT, booleanAttributeValue: false }],
      yes: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_SUICIDE_ATTEMPT, booleanAttributeValue: true }],
      prefer_not_answer: [], // No attributes set for prefer not to answer
    }),
  },

  // Final question - always non-mapped for completion
  {
    id: "study_interest_confirmation",
    type: QuestionType.RADIO,
    question:
      "Based on your answers, you may be eligible for this test study. Are you interested in learning more?",
    required: true,
    allowBack: true,
    quickSelect: {
      label: "Submit",
      setValue: "no",
      submitButtonText: "Submit",
      submitButtonSubtext: {
        allText:
          "All medications in this group will be marked as 'Never taken'",
        remainingText:
          "Remaining medications in this group will be marked as 'Never taken'",
      },
      defaultValueSuffix: "_never",
    },
    options: [
      {
        value: "yes_very_interested",
        label: "Yes, I'm very interested in learning more",
      },
      {
        value: "yes_somewhat_interested",
        label: "Yes, I'm somewhat interested",
      },
      {
        value: "maybe_need_more_info",
        label: "Maybe, I need more information first",
      },
      {
        value: "no_not_interested",
        label: "No, I'm not interested at this time",
      },
    ],
  },
];
